import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus.js';
import { trans } from '@/mixins/i18n.js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { route } from 'ziggy-js';

export const useExportsStore = defineStore('exports', () => {
    const exports = ref({});
    const pollingIntervals = ref({});

    const addExport = (exportData, groupKey, retryParams = null) => {
        if (!exports.value[groupKey]) {
            exports.value[groupKey] = [];
        }

        const existingIndex = exports.value[groupKey].findIndex((exp) => exp.id === exportData.id);

        if (existingIndex === -1) {
            const exportWithRetry = {
                ...exportData,
                _retryParams: retryParams,
            };
            exports.value[groupKey].unshift(exportWithRetry);
            startPolling(exportData.id, groupKey);
        }
    };

    const updateExport = (id, data, groupKey) => {
        if (!exports.value[groupKey]) {
            return;
        }

        const index = exports.value[groupKey].findIndex((exp) => exp.id === id);

        if (index !== -1) {
            const previousProgress = exports.value[groupKey][index].progress ?? 0;
            const merged = { ...exports.value[groupKey][index], ...data };

            // Progress is monotonic: a transient count dip (e.g. a momentarily unavailable batch)
            // must never make the bar jump backwards.
            merged.progress = Math.max(previousProgress, percentComplete(merged));
            exports.value[groupKey][index] = merged;

            const status = merged.status?.value;

            if (status === 'completed' || status === 'failed') {
                stopPolling(id);

                notifyUser(merged);
            }
        }
    };

    const removeExport = (id, groupKey) => {
        if (!exports.value[groupKey]) {
            return;
        }

        const index = exports.value[groupKey].findIndex((exp) => exp.id === id);

        if (index !== -1) {
            exports.value[groupKey].splice(index, 1);
            stopPolling(id);
        }
    };

    const startPolling = (exportId, groupKey) => {
        if (pollingIntervals.value[exportId]) {
            return;
        }

        const poll = async () => {
            try {
                const resp = await $http.get(route('exports.show', exportId));
                updateExport(exportId, resp.data, groupKey);
            } catch (error) {
                console.error('Failed to poll export:', error);
                stopPolling(exportId);
            }
        };

        poll();
        pollingIntervals.value[exportId] = setInterval(poll, 1000);
    };

    const stopPolling = (exportId) => {
        if (pollingIntervals.value[exportId]) {
            clearInterval(pollingIntervals.value[exportId]);
            delete pollingIntervals.value[exportId];
        }
    };

    const notifyUser = (exportData) => {
        const status = exportData.status?.value;
        const notificationType = status === 'failed' ? 'error' : 'success';

        // A batched (PDF) export can finish with fewer items than requested when some renders
        // failed — it's still downloadable, so we warn rather than mark it failed.
        const isPartial =
            status === 'completed' && exportData.expected_count != null && exportData.count != null && exportData.count < exportData.expected_count;

        const link = `<a href="${exportData.url}">${exportData.file_name}</a>`;

        let message;
        if (status === 'failed') {
            message = trans('general.export.failed', { file: exportData.file_name });
        } else if (isPartial) {
            message = trans('general.export.partial', { link, count: exportData.count, total: exportData.expected_count });
        } else {
            message = trans('general.export.ready', { link });
        }

        eventBus.$emit(events.presentFlashMessage, {
            type: notificationType,
            payload: {
                message,
                status: notificationType,
                isSticky: notificationType === 'success',
            },
        });
    };

    const getExportsByGroup = (groupKey) => {
        return computed(() => exports.value[groupKey] || []);
    };

    const percentComplete = (exportData) => {
        if (!exportData.total_count || !exportData.count) {
            return 0;
        }

        return Math.min(100, Math.max(0, Math.round((exportData.count / exportData.total_count) * 100)));
    };

    // Returns the monotonic progress recorded by updateExport, falling back to a live computation
    // for items that haven't been polled yet.
    const getProgress = (exportData) => exportData.progress ?? percentComplete(exportData);

    const clearCompletedExports = (groupKey) => {
        if (!exports.value[groupKey]) {
            return;
        }

        exports.value[groupKey] = exports.value[groupKey].filter((exp) => {
            const isCompleted = exp.status?.value === 'completed' || exp.status?.value === 'failed';

            if (isCompleted) {
                stopPolling(exp.id);
            }

            return !isCompleted;
        });
    };

    const retryExport = async (exportId, groupKey) => {
        const exportItem = exports.value[groupKey]?.find((exp) => exp.id === exportId);

        if (!exportItem?._retryParams) {
            return;
        }

        try {
            const { url } = exportItem._retryParams;
            const resp = await $http.get(url, {});
            removeExport(exportId, groupKey);
            addExport(resp.data, groupKey, exportItem._retryParams);
        } catch (error) {
            console.error('Failed to retry export:', error);
            throw error;
        }
    };

    return {
        exports,
        addExport,
        updateExport,
        removeExport,
        getExportsByGroup,
        getProgress,
        clearCompletedExports,
        retryExport,
    };
});
